import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  MapIcon,
  BookOpenIcon,
  InformationCircleIcon,
  SparklesIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  ClockIcon,
  CameraIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import StoryPeople from '../../components/story/StoryPeople';
import PageView from '../../components/story/PageView';
import { PendingEditsSummary } from '../../components/story/EditConflicts';
import { useAuth } from '../../contexts/AuthContext';
import OutOfWordsSheet, {
  OutOfWordsInfo,
  outOfWordsFrom,
  sharedWordsMessage,
} from '../../components/billing/OutOfWordsSheet';

type Tab = 'overview' | 'characters' | 'outline' | 'chapters' | 'pages' | 'threads' | 'history';

interface Story {
  id: string;
  title: string;
  format: string;
  genres: string[];
  tone: string | null;
  pov: string | null;
  targetLength: string | null;
  status: string;
  wordCount: number;
  characters: any[];
  chapters: any[];
  outline: any | null;
  createdAt: string;
  updatedAt: string;
  /** The signed-in writer's role on this story. */
  role?: 'owner' | 'editor' | 'viewer';
}

interface PlotThread {
  id: string;
  title: string;
  description: string | null;
  status: string;
  introducedChapterId: string | null;
  resolvedChapterId: string | null;
  characterIds: string[] | null;
}

interface Scene {
  id: string;
  chapterId: string;
  summary: string | null;
  povCharacterId: string | null;
  emotionalArc: string | null;
  keyMoments: string | null;
  sortOrder: number;
  content?: string | null;
}

const wordsIn = (text?: string | null) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

const statusColors: Record<string, string> = {
  planned: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-yellow-500/20 text-yellow-400',
  draft: 'bg-green-500/20 text-green-400',
  revised: 'bg-purple-500/20 text-purple-400',
  final: 'bg-emerald-500/20 text-emerald-400',
};

const threadStatusBadge: Record<string, { icon: string; color: string }> = {
  open: { icon: '🔴', color: 'bg-red-500/20 text-red-400' },
  developing: { icon: '🟡', color: 'bg-yellow-500/20 text-yellow-400' },
  resolved: { icon: '✅', color: 'bg-green-500/20 text-green-400' },
};

/** Words behind each length preset — screen pages run ~165-190 words, ~1 page/minute. */
const targetLengthWords: Record<string, number> = {
  short: 5000,
  medium: 20000,
  novel: 60000,
  epic: 100000,
  screen_short: 2500,
  screen_22: 3500,
  screen_30: 5000,
  screen_60: 10000,
  screen_90: 17500,
  screen_120: 23000,
  screen_150: 29000,
  stage_10: 1800,
  stage_30: 5000,
  stage_60: 10000,
  stage_90: 17500,
  stage_120: 23000,
};

/** A write the AI proposed that is not in the manuscript yet, shown apart from committed text. */
const ProposalBox: React.FC<{
  proposal: { mode: 'append' | 'replace'; content: string };
  targetHasText: boolean;
  onReview: () => void;
}> = ({ proposal, targetHasText, onReview }) => {
  const { t } = useTranslation();
  return (
    <div
      className="mt-2 rounded-md p-2"
      style={{
        border: '1px dashed var(--ds-accent, #c4a962)',
        background: 'color-mix(in srgb, var(--ds-accent, #c4a962) 8%, transparent)',
      }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-wide"
        style={{ color: 'var(--ds-text-secondary)' }}
      >
        {!targetHasText
          ? t('stories.scenes.proposedDraft', 'Proposed draft · not committed')
          : proposal.mode === 'append'
            ? t('stories.scenes.proposedAddition', 'Proposed addition · not committed')
            : t('stories.scenes.proposedRewrite', 'Proposed rewrite · not committed')}
      </div>
      <p
        className="text-xs mt-1 whitespace-pre-wrap overflow-hidden italic"
        style={{
          color: 'var(--ds-text-secondary)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {proposal.content}
      </p>
      <button
        onClick={onReview}
        className="text-[11px] font-medium mt-1"
        style={{ color: 'var(--ds-link)' }}
      >
        {t('stories.scenes.reviewProposal', 'Review in chat')}
      </button>
    </div>
  );
};

/** A download filename from the story title: no path or reserved characters, never empty. */
const exportBaseName = (title: string | undefined): string =>
  (title || '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 120)
    .trim() || 'story';

interface ExportOption {
  key: string;
  /** GET /stories/:id/export/<endpoint>. */
  endpoint: string;
  extension: string;
  mime: string;
  labelKey: string;
  fallback: string;
}

const PDF: ExportOption = {
  key: 'pdf',
  endpoint: 'pdf',
  extension: 'pdf',
  mime: 'application/pdf',
  labelKey: 'stories.export.pdf',
  fallback: 'PDF',
};

/** Which files each format exports to, industry formats first. */
const exportOptions = (format: string): ExportOption[] => {
  const common: ExportOption[] = [
    {
      key: 'markdown',
      endpoint: 'markdown',
      extension: 'md',
      mime: 'text/markdown',
      labelKey: 'stories.export.markdown',
      fallback: 'Download Markdown',
    },
    {
      key: 'text',
      endpoint: 'text',
      extension: 'txt',
      mime: 'text/plain',
      labelKey: 'stories.export.text',
      fallback: 'Download Text',
    },
    {
      key: 'html',
      endpoint: 'html',
      extension: 'html',
      mime: 'text/html',
      labelKey: 'stories.export.html',
      fallback: 'Export HTML',
    },
  ];
  if (format === 'screenplay' || format === 'stage_play') {
    return [
      PDF,
      {
        key: 'fdx',
        endpoint: 'fdx',
        extension: 'fdx',
        mime: 'application/xml',
        labelKey: 'stories.export.fdx',
        fallback: 'Final Draft (.fdx)',
      },
      {
        key: 'fountain',
        endpoint: 'screenplay',
        extension: 'fountain',
        mime: 'text/plain',
        labelKey: 'stories.export.fountain',
        fallback: 'Fountain',
      },
      ...common,
    ];
  }
  return [
    {
      key: 'docx',
      endpoint: 'docx',
      extension: 'docx',
      mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      labelKey: 'stories.export.docx',
      fallback: 'Word (.docx)',
    },
    PDF,
    ...common,
  ];
};

const StoryWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [story, setStory] = useState<Story | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  // Bumped whenever the story or a container's scenes reload, so an open page view
  // re-lays out after manuscript changes (never per keystroke: the editor is its own page).
  const [manuscriptRev, setManuscriptRev] = useState(0);

  // Outline state
  const [beginning, setBeginning] = useState('');
  const [middle, setMiddle] = useState('');
  const [end, setEnd] = useState('');
  const [outlineDirty, setOutlineDirty] = useState(false);
  // Mirrored in a ref so fetchStory (a stable callback) can skip clobbering the
  // writer's in-progress outline edits with a late response.
  const outlineDirtyRef = useRef(false);
  const markOutlineDirty = (dirty: boolean) => {
    outlineDirtyRef.current = dirty;
    setOutlineDirty(dirty);
  };
  // Every keystroke bumps this; a save only clears dirty when nothing was typed
  // while its PUT was in flight.
  const outlineEditSeq = useRef(0);
  // The last outline PUT, so a restore can wait for it to settle before acting.
  const outlineSaveInFlight = useRef<Promise<unknown> | null>(null);
  // Every mutating request registers here so a restore can drain ALL writes on the
  // wire — not just outline saves and reorders — before it runs.
  const pendingWrites = useRef(new Set<Promise<unknown>>());
  const trackWrite = <T,>(p: Promise<T>): Promise<T> => {
    const entry: Promise<unknown> = p.catch(() => undefined);
    pendingWrites.current.add(entry);
    entry.finally(() => pendingWrites.current.delete(entry));
    return p;
  };

  // Brainstorm
  const [brainstormPrompt, setBrainstormPrompt] = useState('');
  type ChatAction = {
    tool: string;
    args: Record<string, unknown>;
    status: 'applied' | 'pending' | 'failed' | 'committed' | 'dismissed';
    /** A first draft into empty text (not a rewrite or continuation). */
    draft?: boolean;
    summary: string;
  };
  const [brainstormMessages, setBrainstormMessages] = useState<
    Array<{ role: 'user' | 'ai'; text: string; actions?: ChatAction[]; id?: string }>
  >([]);
  const [brainstorming, setBrainstorming] = useState(false);
  // Continue / Rewrite / Draft dialogue: the beat or chapter/scene the next send is about.
  // The server loads its text itself; only the id and the mode travel.
  type ChatFocus = {
    kind: 'beat' | 'container';
    id: string;
    mode: 'continue' | 'rewrite' | 'draft';
    label: string;
    /** Continue pressed on a beat with no text yet: it is a first draft. */
    fresh?: boolean;
  };
  const [chatFocus, setChatFocus] = useState<ChatFocus | null>(null);
  // A reply on its way owns the thread's tail (its optimistic bubble); a refetch then would
  // drop that bubble, so commits skip theirs and the reply's own refetch catches up.
  const brainstormInFlight = useRef(false);
  // The prompt of the reply on its way: any thread refresh keeps it as the last bubble, so
  // a refresh landing mid-reply (from a commit, in any order) never drops it.
  const inFlightPrompt = useRef<string | null>(null);
  const threadRefetchOwed = useRef(false);
  const [brainstormExpanded, setBrainstormExpanded] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const brainstormBoxRef = useRef<HTMLTextAreaElement | null>(null);
  const [outOfWords, setOutOfWords] = useState<OutOfWordsInfo | null>(null);

  // Brainstorm -> story extraction
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<{
    characters: Array<{
      name: string;
      role: string;
      description: string;
      personality: string;
      existing?: boolean;
    }>;
    plotThreads: Array<{ title: string; description: string; existing?: boolean }>;
  } | null>(null);
  const [extractChecked, setExtractChecked] = useState<Record<string, boolean>>({});
  const [filing, setFiling] = useState(false);
  const [extractSource, setExtractSource] = useState<{ text: string; msgKey: string } | null>(null);
  const [extractNote, setExtractNote] = useState('');
  const [extractedMsgs, setExtractedMsgs] = useState<Record<string, boolean>>({});
  // A late extract response must not reopen a modal the writer already closed
  // (or superseded); only the newest request may publish its result.
  const extractReq = useRef(0);
  // Local replies get a unique id until the server's arrives, so two
  // identical-text replies never share an added-to-story mark.
  const localMsgId = useRef(0);

  // Snapshots (story history)
  const [snapshots, setSnapshots] = useState<
    Array<{ id: string; label: string; wordCount: number; auto?: boolean; createdAt: string }>
  >([]);
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; label: string } | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Only the restore POST itself is uncancellable; during the (unbounded) drain
  // the writer can still back out. Cancelling bumps this token — never the
  // story-wide generation, which would strand unrelated in-flight writes.
  const [restorePosting, setRestorePosting] = useState(false);
  const restoreToken = useRef(0);

  // Character editor
  const [showCharForm, setShowCharForm] = useState(false);

  // Escape closes the chat sheet — but only when it is the topmost overlay; the
  // extract modal, restore confirm, out-of-words sheet, and character editor
  // stack above it.
  const chatOnTop = chatOpen && !extractResult && !restoreTarget && !outOfWords && !showCharForm;
  const chatSheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!chatOnTop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      if (e.key === 'Escape') {
        setChatOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const sheet = chatSheetRef.current;
      if (!sheet) return;
      const focusables = Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!sheet.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatOnTop]);

  // Focus rides the sheet: into the textarea on open, back to the dock on close.
  // The message list opens scrolled to the latest reply.
  const dockButtonRef = useRef<HTMLButtonElement | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const chatWasOpen = useRef(false);
  useEffect(() => {
    if (chatOpen) {
      chatWasOpen.current = true;
      brainstormBoxRef.current?.focus();
    } else if (chatWasOpen.current) {
      chatWasOpen.current = false;
      dockButtonRef.current?.focus();
    }
  }, [chatOpen]);
  useEffect(() => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatOpen, brainstormMessages.length]);
  const [editingChar, setEditingChar] = useState<any>(null);
  const [charName, setCharName] = useState('');
  const [charRole, setCharRole] = useState('supporting');
  const [charDescription, setCharDescription] = useState('');
  const [charPersonality, setCharPersonality] = useState('');

  // Plot threads
  const [threads, setThreads] = useState<PlotThread[]>([]);
  const [showThreadForm, setShowThreadForm] = useState(false);
  const [editingThread, setEditingThread] = useState<PlotThread | null>(null);
  const [threadTitle, setThreadTitle] = useState('');
  const [threadDescription, setThreadDescription] = useState('');
  const [threadStatus, setThreadStatus] = useState('open');

  // Scenes (per chapter)
  const [expandedChapter, setExpandedChapter] = useState<string | null>(null);
  const [chapterScenes, setChapterScenes] = useState<Record<string, Scene[]>>({});
  const [showSceneForm, setShowSceneForm] = useState<string | null>(null);
  const [editingScene, setEditingScene] = useState<Scene | null>(null);
  const [sceneSummary, setSceneSummary] = useState('');
  const [scenePov, setScenePov] = useState('');
  const [sceneArc, setSceneArc] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  // Reordering works on INTENT: an order ref holds the writer's newest desired
  // sequence until it is settled. Taps compose on the ref (state can be a render
  // behind); the queued save composes its payload when its turn RUNS — superseded
  // turns send nothing, so exactly the newest intent reaches the server. Refetches
  // never clobber a pending intent: fresh rows are re-sequenced under it.
  const reorderQueue = useRef<Promise<unknown>>(Promise.resolve());
  const threadReorderSeq = useRef(0);
  const chapterReorderSeq = useRef(0);
  const threadOrderRef = useRef<PlotThread[] | null>(null);
  const chapterOrderRef = useRef<any[] | null>(null);

  // A different story must never inherit this one's pending intent, queued turns,
  // in-flight extraction, or late fetch responses. The generation counter lets every
  // fetcher drop a response that arrives after the story changed.
  const storyGen = useRef(0);
  const idRef = useRef(id);
  useEffect(() => {
    idRef.current = id;
    storyGen.current++;
    threadOrderRef.current = null;
    chapterOrderRef.current = null;
    threadReorderSeq.current++;
    chapterReorderSeq.current++;
    extractReq.current++;
    setExtractResult(null);
    setExtractChecked({});
    setExtractSource(null);
    setExtractNote('');
    setExtractedMsgs({});
    setRestoreTarget(null);
    setShowCharForm(false);
    setShowThreadForm(false);
    setBrainstormPrompt('');
    setChatFocus(null);
    // A reply still on its way belongs to the old story: this one owes it nothing.
    brainstormInFlight.current = false;
    threadRefetchOwed.current = false;
    inFlightPrompt.current = null;
    setSnapshotLabel('');
    // A dirty outline draft from the previous story must neither block the new
    // story's outline from hydrating nor be saveable into it.
    markOutlineDirty(false);
    setBeginning('');
    setMiddle('');
    setEnd('');
    // Another story's in-flight writes must never delay this story's restore.
    outlineSaveInFlight.current = null;
    pendingWrites.current = new Set();
  }, [id]);

  const sequenceUnderIntent = <T extends { id: string }>(rows: T[], intent: T[] | null): T[] => {
    if (!intent) return rows;
    const pos = new Map(intent.map((r, i) => [r.id, i]));
    return [...rows].sort(
      (a, b) =>
        (pos.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (pos.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  };

  const fetchStory = useCallback(async () => {
    // A stale closure (captured under another story) must never fetch or write.
    if (id !== idRef.current) return;
    try {
      const gen = storyGen.current;
      const res = await axios.get(`/stories/${id}`);
      if (gen !== storyGen.current) return;
      if (chapterOrderRef.current && Array.isArray(res.data.chapters)) {
        // Re-sequence fresh rows under the pending intent, then re-group by act (a
        // stable sort, so intent order survives within each act): an act-number edit
        // mid-intent must not leave the ref's physical order out of step with the
        // act-grouped render, or the move buttons' index math goes dead.
        const merged = [...sequenceUnderIntent(res.data.chapters as any[], chapterOrderRef.current)]
          .sort(
            (a: any, b: any) =>
              (a.actNumber ?? Number.MAX_SAFE_INTEGER) - (b.actNumber ?? Number.MAX_SAFE_INTEGER),
          )
          .map((c: any, i: number) => ({ ...c, sortOrder: i }));
        chapterOrderRef.current = merged;
        res.data = { ...res.data, chapters: merged };
      }
      setStory(res.data);
      setManuscriptRev((r) => r + 1);
      if (res.data.outline && !outlineDirtyRef.current) {
        setBeginning(res.data.outline.beginning || '');
        setMiddle(res.data.outline.middle || '');
        setEnd(res.data.outline.end || '');
      }
    } catch {
      toast.error(t('stories.loadError', 'Failed to load story'));
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  const fetchSnapshots = useCallback(async () => {
    // A stale closure (captured under another story) must never fetch or write.
    if (id !== idRef.current) return;
    try {
      const gen = storyGen.current;
      const res = await axios.get(`/stories/${id}/snapshots`);
      if (gen !== storyGen.current) return;
      setSnapshots(res.data);
    } catch {
      /* the tab shows an empty list */
    }
  }, [id]);

  const fetchChatThread = useCallback(async () => {
    // A stale closure (captured under another story) must never fetch or write.
    if (id !== idRef.current) return null;
    try {
      const gen = storyGen.current;
      const res = await axios.get(`/stories/${id}/ai/brainstorm`);
      if (gen !== storyGen.current) return null;
      const stored = res.data.map((m: any) => ({
        role: m.role === 'user' ? 'user' : 'ai',
        text: m.content,
        id: m.id,
        ...(m.actions?.length ? { actions: m.actions } : {}),
      }));
      const waiting = inFlightPrompt.current;
      setBrainstormMessages(
        waiting !== null ? [...stored, { role: 'user', text: waiting }] : stored,
      );
      // Filed extractions are remembered server-side; hydrate the marks.
      setExtractedMsgs((prev) => {
        const next = { ...prev };
        res.data.forEach((m: any) => {
          if (m.extracted) next[m.id] = true;
        });
        return next;
      });
      return res.data as Array<{ id: string; role: string; content: string }>;
    } catch {
      /* the panel starts empty */
      return null;
    }
  }, [id]);

  const fetchThreads = useCallback(async () => {
    // A stale closure (captured under another story) must never fetch or write.
    if (id !== idRef.current) return;
    try {
      const gen = storyGen.current;
      const res = await axios.get(`/stories/${id}/threads`);
      if (gen !== storyGen.current) return;
      if (threadOrderRef.current) {
        const merged = sequenceUnderIntent(res.data as PlotThread[], threadOrderRef.current);
        threadOrderRef.current = merged;
        setThreads(merged);
      } else {
        setThreads(res.data);
      }
    } catch {
      /* ignore */
    }
  }, [id]);

  const fetchScenes = useCallback(
    async (chapterId: string) => {
      if (id !== idRef.current) return;
      try {
        const res = await axios.get(`/stories/${id}/chapters/${chapterId}/scenes`);
        setChapterScenes((prev) => ({ ...prev, [chapterId]: res.data }));
        setManuscriptRev((r) => r + 1);
      } catch {
        /* ignore */
      }
    },
    [id],
  );

  useEffect(() => {
    fetchStory();
    fetchThreads();
    fetchSnapshots();
    fetchChatThread();
  }, [fetchStory, fetchThreads, fetchSnapshots, fetchChatThread]);

  const saveOutline = async () => {
    const gen = storyGen.current;
    const editSeq = outlineEditSeq.current;
    try {
      // Saves chain: a second click queues behind the first, so PUTs can never
      // race each other and the tracked tail always covers every outstanding one
      // (the restore drain waits on exactly this ref).
      const put = (outlineSaveInFlight.current ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => axios.put(`/stories/${id}/outline`, { beginning, middle, end }));
      outlineSaveInFlight.current = put.catch(() => undefined);
      await put;
      if (gen !== storyGen.current) return;
      // Keystrokes that landed while the PUT was in flight were not saved;
      // leaving dirty set keeps the Save button up and the drafts unclobbered.
      if (editSeq === outlineEditSeq.current) markOutlineDirty(false);
      // The guide reads story.outline; keep it (and everything else) in step.
      fetchStory();
      toast.success(t('stories.outlineSaved', 'Outline saved'));
    } catch {
      if (gen !== storyGen.current) return;
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const addChapter = async () => {
    try {
      const sortOrder = (story?.chapters?.length || 0) + 1;
      const res = await trackWrite(
        axios.post(`/stories/${id}/chapters`, {
          title: `Chapter ${sortOrder}`,
          sortOrder,
        }),
      );
      navigate(`/stories/${id}/chapters/${res.data.id}`);
    } catch {
      toast.error(t('stories.createError', 'Failed to create'));
    }
  };

  const saveCharacter = async () => {
    if (!charName.trim()) return;
    const gen = storyGen.current;
    const payload = {
      name: charName,
      role: charRole,
      description: charDescription,
      personality: charPersonality,
    };
    try {
      if (editingChar) {
        await trackWrite(axios.patch(`/stories/${id}/characters/${editingChar.id}`, payload));
      } else {
        await trackWrite(axios.post(`/stories/${id}/characters`, payload));
      }
      if (gen !== storyGen.current) return;
      closeCharForm();
      fetchStory();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const openCharForm = (char: any | null) => {
    setEditingChar(char);
    setCharName(char?.name ?? '');
    setCharRole(char?.role ?? 'supporting');
    setCharDescription(char?.description ?? '');
    setCharPersonality(char?.personality ?? '');
    setShowCharForm(true);
  };

  const closeCharForm = () => {
    setShowCharForm(false);
    setEditingChar(null);
    setCharName('');
    setCharRole('supporting');
    setCharDescription('');
    setCharPersonality('');
  };

  /** Textareas grow with their content. */
  const autoGrow = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  /** Grow a textarea as soon as it mounts with existing content (editing a character). */
  const growOnMount = (el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const deleteCharacter = async (characterId: string) => {
    try {
      await trackWrite(axios.delete(`/stories/${id}/characters/${characterId}`));
      fetchStory();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const deleteChapter = async (chapterId: string) => {
    try {
      await trackWrite(axios.delete(`/stories/${id}/chapters/${chapterId}`));
      fetchStory();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  // Rapid arrow taps are common; the UI updates instantly and the saves run one at
  // a time in order. Each tap identifies its row by ID and composes on an
  // authoritative order ref (state alone can be a render behind under fast double
  // taps), and only the LATEST request of its kind applies its response — or, on
  // failure, reverts by an awaited refetch — so a stale result can never land after
  // a later queued save.

  const moveThread = (threadId: string, delta: number) => {
    const cur = threadOrderRef.current ?? threads;
    const index = cur.findIndex((th) => th.id === threadId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= cur.length) return;
    const next = [...cur];
    [next[index], next[target]] = [next[target], next[index]];
    threadOrderRef.current = next;
    setThreads(next);
    const mySeq = ++threadReorderSeq.current;
    reorderQueue.current = reorderQueue.current.then(async () => {
      // Superseded turns send nothing — a newer turn carries the newest intent.
      if (threadReorderSeq.current !== mySeq) return;
      const order = threadOrderRef.current;
      if (!order) return;
      try {
        const res = await axios.put(`/stories/${id}/threads/reorder`, {
          threadIds: order.map((th) => th.id),
        });
        if (threadReorderSeq.current !== mySeq) return;
        threadOrderRef.current = null;
        setThreads(res.data);
      } catch {
        if (threadReorderSeq.current !== mySeq) return;
        threadOrderRef.current = null;
        await fetchThreads();
        toast.error(t('stories.saveError', 'Failed to save'));
      }
    });
  };

  const moveChapter = (chapterId: string, delta: number) => {
    const cur =
      chapterOrderRef.current ??
      [...(story?.chapters || [])].sort(
        (a: any, b: any) =>
          (a.actNumber ?? Number.MAX_SAFE_INTEGER) - (b.actNumber ?? Number.MAX_SAFE_INTEGER) ||
          a.sortOrder - b.sortOrder,
      );
    const index = cur.findIndex((c: any) => c.id === chapterId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= cur.length) return;
    // Acted formats: a scene stays inside its act.
    if ((cur[index].actNumber ?? null) !== (cur[target].actNumber ?? null)) return;
    const next = [...cur];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((c: any, i: number) => ({ ...c, sortOrder: i }));
    chapterOrderRef.current = renumbered;
    setStory((prev: any) => (prev ? { ...prev, chapters: renumbered } : prev));
    const mySeq = ++chapterReorderSeq.current;
    reorderQueue.current = reorderQueue.current.then(async () => {
      if (chapterReorderSeq.current !== mySeq) return;
      const order = chapterOrderRef.current;
      if (!order) return;
      try {
        const res = await axios.put(`/stories/${id}/chapters/reorder`, {
          chapterIds: order.map((c: any) => c.id),
        });
        if (chapterReorderSeq.current !== mySeq) return;
        chapterOrderRef.current = null;
        setStory((prev: any) => (prev ? { ...prev, chapters: res.data } : prev));
      } catch {
        if (chapterReorderSeq.current !== mySeq) return;
        chapterOrderRef.current = null;
        await fetchStory();
        toast.error(t('stories.saveError', 'Failed to save'));
      }
    });
  };

  const sendBrainstorm = async () => {
    // extracting also blocks: a thread refetch mid-extract would swap message ids
    // out from under the add-to-story flow.
    if (!brainstormPrompt.trim() || brainstorming || extracting || filing) return;
    const gen = storyGen.current;
    const prompt = brainstormPrompt.trim();
    const focus = chatFocus;
    setBrainstormPrompt('');
    setChatFocus(null);
    setBrainstormMessages((prev) => [...prev, { role: 'user', text: prompt }]);
    setBrainstorming(true);
    brainstormInFlight.current = true;
    inFlightPrompt.current = prompt;
    try {
      // The conversation lives server-side now; only the new prompt travels.
      const res = await trackWrite(
        axios.post(`/stories/${id}/ai/brainstorm`, {
          prompt,
          section: tab,
          ...(focus ? { focus: { kind: focus.kind, id: focus.id, mode: focus.mode } } : {}),
        }),
      );
      // The writer may have opened another story while the AI worked; the reply
      // belongs to the old one (safely stored in its thread server-side).
      if (gen !== storyGen.current) return;
      const actions: ChatAction[] = res.data.actions ?? [];
      setBrainstormMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: res.data.content,
          // The server returns the stored row's id; a local one only papers over the
          // rare failure to record the exchange.
          id: res.data.messageId || `local-${++localMsgId.current}`,
          ...(actions.length ? { actions } : {}),
        },
      ]);
      if (actions.length || threadRefetchOwed.current) {
        // Commits address the stored message, so fetch the thread's ids back (and catch up
        // on a commit made while this reply was on its way).
        threadRefetchOwed.current = false;
        fetchChatThread();
      }
      if (res.data.stopped) {
        // The turn kept what it did but ran out of words partway: same sheet / message as a
        // refused send.
        const stopped = { response: { status: 402, data: res.data.stopped } };
        const oow = outOfWordsFrom(stopped);
        const shared = sharedWordsMessage(stopped, t);
        if (oow) setOutOfWords(oow);
        else if (shared) toast.error(shared);
      }
      if (actions.some((a) => a.status === 'applied')) {
        fetchStory();
        fetchThreads();
        fetchSnapshots();
        // Beats live in their own cache; an expanded container must show the AI's work.
        if (expandedChapter) fetchScenes(expandedChapter);
      }
    } catch (err: any) {
      if (gen !== storyGen.current) return;
      // The turn never happened: take it back out of the history and give the prompt back,
      // so a retry cannot duplicate it and the model never sees a failed turn as received.
      setBrainstormMessages((prev) => prev.slice(0, -1));
      // Give the failed prompt back — unless a new draft is already being typed.
      setBrainstormPrompt((current) => current || prompt);
      setChatFocus((current) => current ?? focus);
      const oow = outOfWordsFrom(err);
      const shared = sharedWordsMessage(err, t);
      const missing =
        focus &&
        err?.response?.status === 400 &&
        /^No (beat|chapter\/scene)/.test(String(err?.response?.data?.message ?? ''));
      if (oow) setOutOfWords(oow);
      else if (shared) toast.error(shared);
      else if (missing) {
        // Deleted or moved elsewhere: never let the AI guess — refresh and let the writer pick.
        setChatFocus(null);
        toast.error(
          t(
            'stories.ai.focusMissing',
            "That beat or scene isn't there any more — the page has been refreshed; pick it again.",
          ),
        );
        fetchStory();
        if (expandedChapter) fetchScenes(expandedChapter);
      } else if (err?.response?.status === 503)
        toast.error(t('stories.ai.busy', 'The AI is busy right now — try again in a moment'));
      else toast.error(t('stories.ai.error', 'AI request failed'));
    } finally {
      setBrainstorming(false);
      // A reply from a story the writer has left must not clear this story's flag.
      if (gen === storyGen.current) {
        brainstormInFlight.current = false;
        // The reply (or its failure) is in the thread now; refreshes stop carrying it.
        inFlightPrompt.current = null;
      }
      // However the reply ended, catch up on a commit made while it was on its way (its
      // retired proposals must lose their buttons).
      if (threadRefetchOwed.current && gen === storyGen.current) {
        threadRefetchOwed.current = false;
        fetchChatThread();
      }
    }
  };

  /** Continue / Rewrite / Draft dialogue: open the chat on that item with an editable prompt. */
  const startFocus = (focus: ChatFocus, prompt: string) => {
    setChatFocus(focus);
    setBrainstormPrompt(prompt);
    setChatOpen(true);
  };

  /** Write proposals still waiting for the writer, by the beat or container they target. */
  const pendingProposals = (() => {
    const byTarget: Record<string, Array<{ mode: 'append' | 'replace'; content: string }>> = {};
    for (const m of brainstormMessages) {
      for (const a of m.actions ?? []) {
        if (a.status !== 'pending') continue;
        const target =
          a.tool === 'write_beat_content'
            ? a.args.beatId
            : a.tool === 'write_container_content'
              ? a.args.containerId
              : null;
        if (typeof target !== 'string' || typeof a.args.content !== 'string') continue;
        (byTarget[target] ??= []).push({
          mode: a.args.mode === 'append' ? 'append' : 'replace',
          content: a.args.content,
        });
      }
    }
    return byTarget;
  })();

  const startExtract = async (text: string, msgKey: string, instructions?: string) => {
    if (extracting) return;
    setExtracting(true);
    setExtractSource({ text, msgKey });
    if (!instructions) setExtractNote('');
    const reqId = ++extractReq.current;
    try {
      const res = await axios.post(`/stories/${id}/ai/brainstorm/extract`, {
        text,
        ...(instructions?.trim() ? { instructions: instructions.trim() } : {}),
      });
      if (reqId !== extractReq.current) return;
      const checked: Record<string, boolean> = {};
      // Entities flagged as already existing default to unchecked — filing them again
      // is almost always the accidental double-add the writer complained about.
      res.data.characters?.forEach((c: any, i: number) => (checked[`c${i}`] = !c.existing));
      res.data.plotThreads?.forEach((th: any, i: number) => (checked[`t${i}`] = !th.existing));
      setExtractChecked(checked);
      setExtractResult(res.data);
    } catch (err: any) {
      if (reqId !== extractReq.current) return;
      const oow = outOfWordsFrom(err);
      const shared = sharedWordsMessage(err, t);
      if (oow) setOutOfWords(oow);
      else if (shared) toast.error(shared);
      else toast.error(t('stories.ai.extractError', 'Could not read story elements — try again'));
    } finally {
      setExtracting(false);
    }
  };

  const fileExtract = async () => {
    if (!extractResult || filing) return;
    const gen = storyGen.current;
    setFiling(true);
    const items: Array<{ key: string; save: () => Promise<unknown> }> = [
      ...extractResult.characters.map((c, i) => ({
        key: `c${i}`,
        // Only DTO fields travel — the extraction-only `existing` flag would be
        // rejected by the API's forbidNonWhitelisted validation.
        save: () =>
          trackWrite(
            axios.post(`/stories/${id}/characters`, {
              name: c.name,
              role: c.role,
              description: c.description,
              personality: c.personality,
            }),
          ),
      })),
      ...extractResult.plotThreads.map((th, i) => ({
        key: `t${i}`,
        save: () =>
          trackWrite(
            axios.post(`/stories/${id}/threads`, {
              title: th.title,
              description: th.description,
            }),
          ),
      })),
    ].filter((item) => extractChecked[item.key]);
    let added = 0;
    let failed = false;
    for (const item of items) {
      try {
        await item.save();
        added++;
        // Uncheck as each one lands, so a retry after a failure files only the rest.
        setExtractChecked((prev) => ({ ...prev, [item.key]: false }));
      } catch {
        failed = true;
        break;
      }
    }
    if (gen !== storyGen.current) {
      setFiling(false);
      return;
    }
    if (added > 0) {
      fetchStory();
      fetchThreads();
    }
    if (!failed) {
      toast.success(
        t('stories.ai.extractAdded', 'Added {{count}} to your story', { count: added }),
      );
      // Close the modal before the persistence round-trips below: nothing in that tail
      // may touch modal state, or a slow response could clobber a NEWER extraction the
      // writer has since opened.
      setExtractNote('');
      setExtractResult(null);
      extractReq.current++;
      if (extractSource) {
        // The background thread refetch may have swapped a local id for the server's;
        // mark whatever identity the message carries NOW, not the one captured at click.
        const live =
          brainstormMessages.find((m) => m.id === extractSource.msgKey) ??
          [...brainstormMessages]
            .reverse()
            .find((m) => m.role === 'ai' && m.text === extractSource.text);
        const key = live ? live.id || live.text : extractSource.msgKey;
        setExtractedMsgs((prev) => ({ ...prev, [key]: true }));
        // Persist the mark so a reload can't re-offer the same extraction. Local ids
        // aren't known to the server; refetch the thread to learn the real one.
        let serverId = key && !key.startsWith('local-') && live?.id === key ? key : null;
        if (!serverId) {
          const rows = await fetchChatThread();
          const row = rows
            ?.slice()
            .reverse()
            .find((m) => m.role === 'assistant' && m.content === extractSource.text);
          serverId = row?.id ?? null;
          if (serverId) setExtractedMsgs((prev) => ({ ...prev, [serverId as string]: true }));
        }
        if (serverId) {
          let persisted = false;
          for (let attempt = 0; attempt < 2 && !persisted; attempt++) {
            try {
              await trackWrite(
                axios.post(`/stories/${id}/ai/brainstorm/extracted`, {
                  messageId: serverId,
                }),
              );
              persisted = true;
            } catch {
              /* retry once */
            }
          }
          if (!persisted) {
            toast(
              t(
                'stories.ai.markPersistFailed',
                'Added to your story — but the message may offer "Add to story" again after a reload.',
              ),
            );
          }
        } else {
          // Resolution itself failed (thread refetch error): warn the same way instead
          // of silently dropping the mark.
          toast(
            t(
              'stories.ai.markPersistFailed',
              'Added to your story — but the message may offer "Add to story" again after a reload.',
            ),
          );
        }
      }
    } else {
      toast.error(
        t('stories.ai.extractPartial', 'Added {{count}}; the rest failed — try again', {
          count: added,
        }),
      );
    }
    setFiling(false);
  };

  const clearChat = async () => {
    const gen = storyGen.current;
    if (extracting || filing) return;
    try {
      await axios.delete(`/stories/${id}/ai/brainstorm`);
      if (gen !== storyGen.current) return;
      setBrainstormMessages([]);
      setExtractedMsgs({});
      setExtractSource(null);
      // An extraction still in flight belongs to the conversation just deleted.
      extractReq.current++;
      setExtractResult(null);
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const takeSnapshot = async () => {
    const gen = storyGen.current;
    if (takingSnapshot) return;
    setTakingSnapshot(true);
    try {
      await axios.post(`/stories/${id}/snapshots`, { label: snapshotLabel.trim() || undefined });
      if (gen !== storyGen.current) return;
      setSnapshotLabel('');
      toast.success(t('stories.history.saved', 'Snapshot saved'));
      fetchSnapshots();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    } finally {
      setTakingSnapshot(false);
    }
  };

  const restoreSnapshot = async () => {
    if (!restoreTarget || restoring) return;
    const gen = storyGen.current;
    const token = restoreToken.current;
    setRestoring(true);
    try {
      // Writes already on the wire must land BEFORE the restore runs, or a slow
      // outline save / reorder PUT could overwrite the restored content after the
      // fact with no client-side gate able to stop it. Drain them first; the
      // restore endpoint snapshots current state before applying, so their
      // results stay recoverable either way.
      await reorderQueue.current.catch(() => undefined);
      await outlineSaveInFlight.current?.catch(() => undefined);
      await Promise.all([...pendingWrites.current]).catch(() => undefined);
      // Navigating, cancelling, or anything else that moved the generation or the
      // restore token during the drain aborts BEFORE the POST.
      if (gen !== storyGen.current || token !== restoreToken.current) return;
      // The dialog promises nothing is lost; an unsaved outline draft lives only
      // here, so persist it first — the restore endpoint snapshots server state.
      if (outlineDirtyRef.current) {
        await axios.put(`/stories/${id}/outline`, { beginning, middle, end });
        if (gen !== storyGen.current || token !== restoreToken.current) return;
      }
      setRestorePosting(true);
      await axios.post(`/stories/${id}/snapshots/${restoreTarget.id}/restore`);
      if (gen !== storyGen.current) return;
      // The restored state is now the truth: invalidate every in-flight writer
      // started before it (a pending outline save must not clear dirty, toast
      // 'saved', or rehydrate over the restore) AND every pending reorder intent —
      // the refetch below must show the snapshot's order, not re-sequence it under
      // a pre-restore intent, and a queued reorder PUT must not reimpose it.
      storyGen.current++;
      threadOrderRef.current = null;
      chapterOrderRef.current = null;
      threadReorderSeq.current++;
      chapterReorderSeq.current++;
      toast.success(t('stories.history.restored', 'Story restored'));
      setRestoreTarget(null);
      // Drop every cache the restore may have replaced: outline inputs repopulate from the
      // fresh fetch (and clear when the snapshot had none), scenes refetch on expand.
      setBeginning('');
      setMiddle('');
      setEnd('');
      markOutlineDirty(false);
      setChapterScenes({});
      setExpandedChapter(null);
      fetchStory();
      fetchThreads();
      fetchSnapshots();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    } finally {
      setRestoring(false);
      setRestorePosting(false);
    }
  };

  const deleteSnapshot = async (snapshotId: string) => {
    try {
      await axios.delete(`/stories/${id}/snapshots/${snapshotId}`);
      fetchSnapshots();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const [committing, setCommitting] = useState<string | null>(null);
  const commitAction = async (msgIndex: number, actionIndex: number, action: ChatAction) => {
    const gen = storyGen.current;
    const messageId = brainstormMessages[msgIndex]?.id;
    if (!messageId) {
      toast.error(t('stories.saveError', 'Failed to save'));
      return;
    }
    const key = `${messageId}:${actionIndex}`;
    if (committing) return;
    setCommitting(key);
    void action;
    try {
      const res = await trackWrite(
        axios.post(`/stories/${id}/ai/actions/commit`, {
          messageId,
          actionIndex,
        }),
      );
      if (gen !== storyGen.current) return;
      setBrainstormMessages((prev) =>
        prev.map((m, mi) =>
          mi === msgIndex && m.actions
            ? {
                ...m,
                actions: m.actions.map((a, ai) =>
                  ai === actionIndex
                    ? { ...a, status: 'committed' as const, summary: res.data.summary }
                    : a,
                ),
              }
            : m,
        ),
      );
      toast.success(res.data.summary);
      // A commit retires other proposals for the same beat server-side; show that.
      if (brainstormInFlight.current) threadRefetchOwed.current = true;
      else fetchChatThread();
      fetchStory();
      fetchThreads();
      fetchSnapshots();
      if (expandedChapter) fetchScenes(expandedChapter);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
      // Out of date (the text changed under it): the server retired it; show that.
      if (err?.response?.status === 409 && gen === storyGen.current) {
        if (brainstormInFlight.current) threadRefetchOwed.current = true;
        else fetchChatThread();
      }
    } finally {
      setCommitting(null);
    }
  };

  const dismissAction = async (msgIndex: number, actionIndex: number) => {
    const gen = storyGen.current;
    const messageId = brainstormMessages[msgIndex]?.id;
    if (!messageId || committing) return;
    setCommitting(`${messageId}:${actionIndex}`);
    try {
      await trackWrite(axios.post(`/stories/${id}/ai/actions/dismiss`, { messageId, actionIndex }));
      if (gen !== storyGen.current) return;
      setBrainstormMessages((prev) =>
        prev.map((m, mi) =>
          mi === msgIndex && m.actions
            ? {
                ...m,
                actions: m.actions.map((a, ai) =>
                  ai === actionIndex ? { ...a, status: 'dismissed' as any } : a,
                ),
              }
            : m,
        ),
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    } finally {
      setCommitting(null);
    }
  };

  // Plot thread handlers
  const saveThread = async () => {
    if (!threadTitle.trim()) return;
    const gen = storyGen.current;
    try {
      if (editingThread) {
        await trackWrite(
          axios.patch(`/stories/${id}/threads/${editingThread.id}`, {
            title: threadTitle,
            description: threadDescription || undefined,
            status: threadStatus,
          }),
        );
      } else {
        await trackWrite(
          axios.post(`/stories/${id}/threads`, {
            title: threadTitle,
            description: threadDescription || undefined,
            status: threadStatus,
          }),
        );
      }
      if (gen !== storyGen.current) return;
      setShowThreadForm(false);
      setEditingThread(null);
      setThreadTitle('');
      setThreadDescription('');
      setThreadStatus('open');
      fetchThreads();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const deleteThread = async (threadId: string) => {
    try {
      await trackWrite(axios.delete(`/stories/${id}/threads/${threadId}`));
      fetchThreads();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  // Scene handlers
  const saveScene = async (chapterId: string) => {
    if (!sceneSummary.trim()) return;
    const gen = storyGen.current;
    try {
      if (editingScene) {
        await trackWrite(
          axios.patch(`/stories/${id}/chapters/${chapterId}/scenes/${editingScene.id}`, {
            summary: sceneSummary,
            povCharacterId: scenePov || undefined,
            emotionalArc: sceneArc || undefined,
          }),
        );
      } else {
        await trackWrite(
          axios.post(`/stories/${id}/chapters/${chapterId}/scenes`, {
            summary: sceneSummary,
            povCharacterId: scenePov || undefined,
            emotionalArc: sceneArc || undefined,
          }),
        );
      }
      if (gen !== storyGen.current) return;
      setShowSceneForm(null);
      setEditingScene(null);
      setSceneSummary('');
      setScenePov('');
      setSceneArc('');
      fetchScenes(chapterId);
      fetchStory();
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  const deleteScene = async (chapterId: string, sceneId: string) => {
    try {
      await trackWrite(axios.delete(`/stories/${id}/chapters/${chapterId}/scenes/${sceneId}`));
      fetchScenes(chapterId);
      fetchStory();
    } catch {
      toast.error(t('stories.deleteError', 'Failed to delete'));
    }
  };

  const moveScene = async (chapterId: string, sceneId: string, direction: -1 | 1) => {
    const scenes = chapterScenes[chapterId] || [];
    const idx = scenes.findIndex((s) => s.id === sceneId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= scenes.length) return;
    const ids = scenes.map((s) => s.id);
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try {
      await trackWrite(
        axios.put(`/stories/${id}/chapters/${chapterId}/scenes/reorder`, {
          sceneIds: ids,
        }),
      );
      fetchScenes(chapterId);
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    }
  };

  // Export handlers. Fetched as a blob so binary formats (PDF, Word) arrive byte-for-byte.
  const downloadFile = async (format: string, extension: string, mimeType: string) => {
    setExporting(true);
    try {
      const res = await axios.get(`/stories/${id}/export/${format}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportBaseName(story?.title)}.${extension}`;
      a.click();
      // Safari can drop the download if the URL is revoked in the same tick.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      // A blob response carries the API's JSON error as a Blob too.
      let message: string | undefined;
      try {
        const data = err?.response?.data;
        if (data instanceof Blob) message = JSON.parse(await data.text())?.message;
      } catch {
        /* not JSON */
      }
      toast.error(
        typeof message === 'string' && message
          ? message
          : t('stories.exportError', 'Export failed'),
      );
    } finally {
      setExporting(false);
    }
  };

  const openHtmlExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`/stories/${id}/export/html`, { responseType: 'text' });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error(t('stories.exportError', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  if (loading || !story) {
    return (
      <MobileLayout showBackButton>
        <div className="flex-1 flex items-center justify-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
          />
        </div>
      </MobileLayout>
    );
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'overview',
      label: t('stories.tabs.overview', 'Overview'),
      icon: <InformationCircleIcon className="w-4 h-4" />,
    },
    {
      key: 'characters',
      label: t('stories.tabs.characters', 'Characters'),
      icon: <UserGroupIcon className="w-4 h-4" />,
    },
    {
      key: 'outline',
      label: t('stories.tabs.outline', 'Outline'),
      icon: <MapIcon className="w-4 h-4" />,
    },
    {
      key: 'chapters',
      label:
        story.format === 'screenplay' || story.format === 'stage_play'
          ? t('stories.structure.scenes', 'Scenes')
          : t('stories.tabs.chapters', 'Chapters'),
      icon: <BookOpenIcon className="w-4 h-4" />,
    },
    {
      key: 'pages',
      label: t('stories.tabs.pageView', 'Page view'),
      icon: <DocumentTextIcon className="w-4 h-4" />,
    },
    {
      key: 'threads',
      label: t('stories.tabs.threads', 'Plot Threads'),
      icon: <LinkIcon className="w-4 h-4" />,
    },
    {
      key: 'history',
      label: t('stories.tabs.history', 'History'),
      icon: <ClockIcon className="w-4 h-4" />,
    },
  ];

  const totalWords =
    story.chapters?.reduce((sum: number, c: any) => sum + (c.wordCount || 0), 0) || 0;
  const targetWords = targetLengthWords[story.targetLength || ''] || 0;
  // Acted formats read the hierarchy as Act -> Scene -> Beat; prose as Part -> Chapter -> Scene.
  const acted = story.format === 'screenplay' || story.format === 'stage_play';
  const vocab = {
    container: acted
      ? t('stories.structure.scene', 'Scene')
      : t('stories.structure.chapter', 'Chapter'),
    containers: acted
      ? t('stories.structure.scenes', 'Scenes')
      : t('stories.structure.chapters', 'Chapters'),
    subs: acted
      ? t('stories.structure.beats', 'Beats')
      : t('stories.structure.scenesSub', 'Scenes'),
    addContainer: acted
      ? t('stories.structure.addScene', 'Add Scene')
      : t('stories.addChapter', 'Add Chapter'),
    group: acted ? t('stories.structure.act', 'Act') : t('stories.structure.part', 'Part'),
  };
  const progressPct =
    targetWords > 0 ? Math.min(100, Math.round((totalWords / targetWords) * 100)) : 0;

  // Note 23: gently guide the writer to the next step until the story is done —
  // cast (5 filled out) → arc → threads → outline every scene → write every scene.
  const guide = (() => {
    const chars = story.characters || [];
    const filled = chars.filter((c: any) => (c.description || '').trim()).length;
    if (filled < 5) {
      return {
        tab: 'characters' as Tab,
        text: t(
          'stories.guide.characters',
          'Next: flesh out your cast — {{filled}} of 5 characters have descriptions',
          { filled },
        ),
      };
    }
    const o = story.outline;
    if (!o || !(o.beginning || '').trim() || !(o.middle || '').trim() || !(o.end || '').trim()) {
      return {
        tab: 'outline' as Tab,
        text: t('stories.guide.outline', 'Next: sketch the overall arc — beginning, middle, end'),
      };
    }
    if (threads.length === 0) {
      return {
        tab: 'threads' as Tab,
        text: t(
          'stories.guide.threads',
          'Next: add a plot thread or two to weave through the story',
        ),
      };
    }
    const chapters = story.chapters || [];
    if (chapters.length === 0) {
      return {
        tab: 'chapters' as Tab,
        text: t('stories.guide.scenesNone', 'Next: break the story into {{containers}}', {
          containers: vocab.containers,
        }),
      };
    }
    // Fully outlined: at least one beat and every beat summarized — or already
    // written (a novelist drafting straight into the chapter never makes beats;
    // the guide must not demand outlines for finished pages).
    const outlined = chapters.filter(
      (c: any) =>
        ((c.beats ?? 0) > 0 && (c.outlinedBeats ?? 0) === (c.beats ?? 0)) || (c.wordCount || 0) > 0,
    ).length;
    if (outlined < chapters.length) {
      return {
        tab: 'chapters' as Tab,
        text: t(
          'stories.guide.sceneOutlines',
          'Outline every {{container}} before dialogue — {{done}} of {{total}} outlined',
          { container: vocab.container, done: outlined, total: chapters.length },
        ),
      };
    }
    const written = chapters.filter((c: any) => (c.wordCount || 0) > 0).length;
    if (written < chapters.length) {
      return {
        tab: 'chapters' as Tab,
        text: t(
          'stories.guide.dialogue',
          'Every {{container}} is outlined — time to write! {{written}} of {{total}} have pages',
          { container: vocab.container, written, total: chapters.length },
        ),
      };
    }
    return {
      tab: null as Tab | null,
      text: t('stories.guide.done', 'Outlined and written, every {{container}} — it’s done! 🎉', {
        container: vocab.container,
      }),
    };
  })();
  // The cast list reads in order of narrative importance.
  const roleRank: Record<string, number> = {
    protagonist: 0,
    antagonist: 1,
    love_interest: 2,
    mentor: 3,
    sidekick: 4,
    supporting: 5,
    minor: 6,
  };

  // Viewers read (and export); every control that changes the story is hidden from them.
  // The API enforces the same rule.
  const canEdit = story.role !== 'viewer';
  const isOwner = (story.role ?? 'owner') === 'owner';

  return (
    <MobileLayout headerTitle={story.title} showBackButton>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div
          className="flex border-b px-2 overflow-x-auto"
          style={{ borderColor: 'var(--ds-border)' }}
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key ? 'border-primary-500 text-primary-500' : 'border-transparent'
              }`}
              style={tab !== t.key ? { color: 'var(--ds-text-secondary)' } : {}}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {/* Note 23: the gentle nudge toward the next step, on every tab. */}
        {!canEdit && (
          <div className="px-5 pt-2">
            <div
              className="w-full flex items-center gap-2 py-2 px-3 rounded-lg text-xs"
              style={{
                background: 'color-mix(in srgb, var(--ds-accent, #8b5cf6) 8%, transparent)',
                color: 'var(--ds-text-secondary)',
              }}
            >
              <span aria-hidden="true">👁</span>
              <span className="flex-1">
                {t(
                  'stories.members.viewerBanner',
                  'You can read this story. Only editors can change it.',
                )}
              </span>
            </div>
          </div>
        )}
        {canEdit && (
          <div className="px-5 pt-2">
            <button
              onClick={() => guide.tab && setTab(guide.tab)}
              disabled={!guide.tab}
              className="w-full text-left flex items-center gap-2 py-2 px-3 rounded-lg text-xs"
              style={{
                background: 'color-mix(in srgb, var(--ds-accent, #8b5cf6) 8%, transparent)',
                color: 'var(--ds-text-secondary)',
              }}
            >
              <span aria-hidden="true">🧭</span>
              <span className="flex-1">{guide.text}</span>
              {guide.tab && guide.tab !== tab && (
                <span aria-hidden="true" style={{ color: 'var(--ds-link)' }}>
                  →
                </span>
              )}
            </button>
          </div>
        )}

        {/* Always in reach: stuck writers get pulled into the workshop chat. */}
        {canEdit && !chatOpen && (
          <div className="px-5 pt-3">
            <button
              onClick={() => setChatOpen(true)}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-sm font-medium border transition-colors hover:border-primary-500"
              style={{
                borderColor: 'var(--ds-border)',
                background: 'var(--ds-bg-card)',
                color: 'var(--ds-text-primary)',
              }}
            >
              <SparklesIcon className="w-4 h-4 text-primary-400" />
              {t('stories.ai.feelingStuck', 'Feeling stuck? Just brainstorm')}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 pb-16">
          {/* Overview Tab */}
          {tab === 'overview' && (
            <div className="space-y-4">
              <div
                className="ds-card rounded-2xl p-5"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <h2
                  className="text-xl font-semibold mb-3"
                  style={{ color: 'var(--ds-text-primary)' }}
                >
                  {story.title}
                </h2>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span style={{ color: 'var(--ds-text-secondary)' }}>
                      {t('stories.formatLabel', 'Format')}:{' '}
                    </span>
                    <span style={{ color: 'var(--ds-text-primary)' }}>{story.format}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--ds-text-secondary)' }}>
                      {t('stories.statusLabel', 'Status')}:{' '}
                    </span>
                    <span style={{ color: 'var(--ds-text-primary)' }}>{story.status}</span>
                  </div>
                  {story.tone && (
                    <div>
                      <span style={{ color: 'var(--ds-text-secondary)' }}>
                        {t('stories.toneLabel', 'Tone')}:{' '}
                      </span>
                      <span style={{ color: 'var(--ds-text-primary)' }}>{story.tone}</span>
                    </div>
                  )}
                  {story.pov && (
                    <div>
                      <span style={{ color: 'var(--ds-text-secondary)' }}>
                        {t('stories.povLabel', 'POV')}:{' '}
                      </span>
                      <span style={{ color: 'var(--ds-text-primary)' }}>{story.pov}</span>
                    </div>
                  )}
                </div>
                {story.genres?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {story.genres.map((g) => (
                      <span
                        key={g}
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: 'var(--ds-border)',
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: t('stories.words', 'Words'), value: totalWords.toLocaleString() },
                  {
                    label: t('stories.tabs.characters', 'Characters'),
                    value: story.characters?.length || 0,
                  },
                  {
                    label: t('stories.tabs.chapters', 'Chapters'),
                    value: story.chapters?.length || 0,
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="ds-card rounded-xl p-4 text-center"
                    style={{
                      background: 'var(--ds-bg-card)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                      {s.value}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Word count progress */}
              {targetWords > 0 && (
                <div
                  className="ds-card rounded-2xl p-5"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--ds-text-primary)' }}
                    >
                      {t('stories.progress', 'Progress')}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                      {totalWords.toLocaleString()} / {targetWords.toLocaleString()}{' '}
                      {t('stories.words', 'words')} ({progressPct}%)
                    </span>
                  </div>
                  <div
                    className="w-full h-3 rounded-full overflow-hidden"
                    style={{ background: 'var(--ds-border)' }}
                  >
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Export */}
              <div
                className="ds-card rounded-2xl p-5"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ArrowDownTrayIcon className="w-5 h-5 text-primary-400" />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                    {t('stories.export.title', 'Export')}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {exportOptions(story.format).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() =>
                        opt.key === 'html'
                          ? openHtmlExport()
                          : downloadFile(opt.endpoint, opt.extension, opt.mime)
                      }
                      disabled={exporting}
                      className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500 disabled:opacity-50"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    >
                      {t(opt.labelKey, opt.fallback)}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-3" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('stories.export.authorHint', 'Title pages use your author details.')}{' '}
                  <Link to="/settings/author-details" style={{ color: 'var(--ds-link)' }}>
                    {t('stories.export.authorHintLink', 'Edit them')}
                  </Link>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => setTab('pages')}
                    style={{ color: 'var(--ds-link)' }}
                  >
                    {t('stories.export.preview', 'Preview the pages')}
                  </button>
                </p>
              </div>

              {canEdit && (
                <PendingEditsSummary
                  storyId={story.id}
                  isOwner={isOwner}
                  onOpen={(chapterId) => navigate(`/stories/${id}/chapters/${chapterId}`)}
                />
              )}
              <StoryPeople storyId={story.id} currentUserId={user?.id} />
            </div>
          )}

          {/* Characters Tab */}
          {tab === 'characters' && (
            <div className="space-y-3">
              {canEdit && (
                <button
                  onClick={() => openCharForm(null)}
                  className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                  style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                >
                  <PlusIcon className="w-4 h-4 inline mr-1" />
                  {t('stories.addCharacter', 'Add Character')}
                </button>
              )}

              {[...(story.characters || [])]
                .sort(
                  (a: any, b: any) =>
                    (roleRank[a.role] ?? roleRank.supporting) -
                    (roleRank[b.role] ?? roleRank.supporting),
                )
                .map((char) => (
                  <div
                    key={char.id}
                    className="ds-card rounded-xl p-4 flex items-start justify-between gap-3"
                    style={{
                      background: 'var(--ds-bg-card)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    <button className="flex-1 text-left" onClick={() => openCharForm(char)}>
                      <div className="font-medium" style={{ color: 'var(--ds-text-primary)' }}>
                        {char.name}
                      </div>
                      <div
                        className="text-xs capitalize"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      >
                        {char.role?.replace('_', ' ')}
                      </div>
                      {char.description && (
                        <p
                          className="text-xs mt-1 overflow-hidden"
                          style={{
                            color: 'var(--ds-text-secondary)',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {char.description}
                        </p>
                      )}
                    </button>
                    {canEdit && (
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => openCharForm(char)}>
                          <PencilIcon
                            className="w-4 h-4"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          />
                        </button>
                        <button onClick={() => deleteCharacter(char.id)}>
                          <TrashIcon className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}

              {(!story.characters || story.characters.length === 0) && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.noCharacters', 'No characters yet. Add your first one!')}
                </p>
              )}
            </div>
          )}

          {/* Character editor: one character takes the screen; the rest stay readable */}
          {showCharForm && (
            <div
              className="fixed inset-0 z-50 flex p-3 md:p-6"
              style={{ background: 'rgba(0,0,0,0.6)' }}
            >
              <div
                className="m-auto w-full max-w-5xl max-h-[92vh] flex flex-col md:flex-row rounded-2xl overflow-hidden"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <div className="flex-1 p-5 overflow-y-auto space-y-3">
                  <div className="flex items-center justify-between">
                    <h3
                      className="text-base font-semibold"
                      style={{ color: 'var(--ds-text-primary)' }}
                    >
                      {editingChar
                        ? t('stories.editCharacter', 'Edit character')
                        : t('stories.addCharacter', 'Add Character')}
                    </h3>
                    <button onClick={closeCharForm} aria-label={t('common.close', 'Close')}>
                      <XMarkIcon
                        className="w-5 h-5"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={charName}
                    disabled={!canEdit}
                    onChange={(e) => setCharName(e.target.value)}
                    placeholder={t('stories.characterName', 'Character name')}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <select
                    value={charRole}
                    disabled={!canEdit}
                    onChange={(e) => setCharRole(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    {[
                      'protagonist',
                      'antagonist',
                      'love_interest',
                      'mentor',
                      'sidekick',
                      'supporting',
                      'minor',
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('stories.characterDescription', 'Description')}
                    </label>
                    <textarea
                      ref={growOnMount}
                      value={charDescription}
                      disabled={!canEdit}
                      onChange={(e) => setCharDescription(e.target.value)}
                      onInput={autoGrow}
                      rows={8}
                      placeholder={t(
                        'stories.characterDescPlaceholder',
                        'Write as much as you like. The AI reads all of it when it writes with you.',
                      )}
                      className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm resize-y min-h-[12rem]"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('stories.characterPersonality', 'Personality & voice')}
                    </label>
                    <textarea
                      ref={growOnMount}
                      value={charPersonality}
                      disabled={!canEdit}
                      onChange={(e) => setCharPersonality(e.target.value)}
                      onInput={autoGrow}
                      rows={4}
                      placeholder={t(
                        'stories.characterPersonalityPlaceholder',
                        'How they talk, quirks, mannerisms...',
                      )}
                      className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm resize-y min-h-[6rem]"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    {canEdit && (
                      <button
                        onClick={saveCharacter}
                        disabled={!charName.trim()}
                        className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                      >
                        {editingChar ? t('common.save', 'Save') : t('common.add', 'Add')}
                      </button>
                    )}
                    <button
                      onClick={closeCharForm}
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
                {(story.characters?.filter((c: any) => c.id !== editingChar?.id).length ?? 0) >
                  0 && (
                  <div
                    className="w-full md:w-80 shrink-0 border-t md:border-t-0 md:border-l p-4 overflow-y-auto max-h-48 md:max-h-none"
                    style={{
                      borderColor: 'var(--ds-border)',
                      background: 'var(--ds-bg-primary)',
                    }}
                  >
                    <div
                      className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('stories.otherCharacters', 'Other characters')}
                    </div>
                    <div className="space-y-4">
                      {story.characters
                        ?.filter((c: any) => c.id !== editingChar?.id)
                        .map((c: any) => (
                          <div key={c.id}>
                            <div
                              className="text-sm font-medium"
                              style={{ color: 'var(--ds-text-primary)' }}
                            >
                              {c.name}
                              <span
                                className="ml-2 text-xs font-normal capitalize"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              >
                                {c.role?.replace('_', ' ')}
                              </span>
                            </div>
                            {(c.description || c.personality) && (
                              <p
                                className="text-xs mt-0.5 whitespace-pre-wrap"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              >
                                {[c.description, c.personality].filter(Boolean).join('\n')}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Page View Tab */}
          {tab === 'pages' && (
            // Keyed by story: another story's pages must never linger while this one loads.
            <PageView
              key={story.id}
              storyId={story.id}
              format={story.format}
              refreshKey={manuscriptRev}
            />
          )}

          {/* History Tab */}
          {tab === 'history' && (
            <div className="space-y-3">
              <div
                className="ds-card rounded-xl p-4 space-y-2"
                style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
              >
                <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t(
                    'stories.history.hint',
                    'A snapshot saves the whole story — pages, characters, outline, threads. Restore any of them later; the state you replace is kept automatically.',
                  )}
                </p>
                {canEdit && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={snapshotLabel}
                      onChange={(e) => setSnapshotLabel(e.target.value)}
                      maxLength={120}
                      placeholder={t(
                        'stories.history.labelPlaceholder',
                        'Label, like a commit message',
                      )}
                      className="flex-1 px-3 py-2 rounded-lg border bg-transparent text-sm"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    />
                    <button
                      onClick={takeSnapshot}
                      disabled={takingSnapshot}
                      className="px-3 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-1"
                    >
                      <CameraIcon className="w-4 h-4" />
                      {t('stories.history.take', 'Take snapshot')}
                    </button>
                  </div>
                )}
              </div>

              {snapshots.map((snap) => (
                <div
                  key={snap.id}
                  className="ds-card rounded-xl p-4 flex items-center justify-between gap-3"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <div className="min-w-0">
                    <div
                      className="font-medium text-sm truncate"
                      style={{ color: 'var(--ds-text-primary)' }}
                    >
                      {snap.label}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                      {new Date(snap.createdAt).toLocaleString()} ·{' '}
                      {t('stories.history.words', '{{count}} words', {
                        count: snap.wordCount,
                      })}
                      {snap.auto && <> · {t('stories.history.auto', 'auto')}</>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 items-center">
                    {canEdit && (
                      <button
                        onClick={() => setRestoreTarget({ id: snap.id, label: snap.label })}
                        className="px-3 py-1.5 rounded-lg border text-xs font-medium hover:border-primary-500"
                        style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                      >
                        {t('stories.history.restore', 'Restore')}
                      </button>
                    )}
                    {isOwner && (
                      <button onClick={() => deleteSnapshot(snap.id)}>
                        <TrashIcon className="w-4 h-4 text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {snapshots.length === 0 && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.history.empty', 'No snapshots yet — take one before a big change.')}
                </p>
              )}
            </div>
          )}

          {/* Outline Tab */}
          {tab === 'outline' && (
            <div className="space-y-4">
              {[
                {
                  label: t('stories.outline.beginning', 'Beginning'),
                  value: beginning,
                  set: setBeginning,
                },
                { label: t('stories.outline.middle', 'Middle'), value: middle, set: setMiddle },
                { label: t('stories.outline.end', 'End'), value: end, set: setEnd },
              ].map((section) => (
                <div key={section.label}>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: 'var(--ds-text-primary)' }}
                  >
                    {section.label}
                  </label>
                  <textarea
                    value={section.value}
                    readOnly={!canEdit}
                    onChange={(e) => {
                      section.set(e.target.value);
                      outlineEditSeq.current++;
                      markOutlineDirty(true);
                    }}
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                    placeholder={t('stories.outline.placeholder', 'Describe this section...')}
                  />
                </div>
              ))}
              {canEdit && outlineDirty && (
                <button
                  onClick={saveOutline}
                  className="w-full py-3 bg-primary-500 text-white rounded-xl font-semibold"
                >
                  {t('stories.saveOutline', 'Save Outline')}
                </button>
              )}
            </div>
          )}

          {/* Chapters Tab */}
          {tab === 'chapters' && (
            <div className="space-y-3">
              {canEdit && (
                <button
                  onClick={addChapter}
                  className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                  style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                >
                  <PlusIcon className="w-4 h-4 inline mr-1" />
                  {vocab.addContainer}
                </button>
              )}

              {story.chapters
                ?.sort(
                  (a: any, b: any) =>
                    (a.actNumber ?? Number.MAX_SAFE_INTEGER) -
                      (b.actNumber ?? Number.MAX_SAFE_INTEGER) || a.sortOrder - b.sortOrder,
                )
                .map((chapter: any, idx: number, arr: any[]) => (
                  <div key={chapter.id}>
                    {chapter.actNumber && arr[idx - 1]?.actNumber !== chapter.actNumber && (
                      <div
                        className="text-xs font-semibold uppercase tracking-wide mt-4 mb-1"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      >
                        {vocab.group} {chapter.actNumber}
                      </div>
                    )}
                    <div
                      className="ds-card rounded-xl p-4 flex items-center justify-between cursor-pointer hover:scale-[1.01] transition-transform"
                      style={{
                        background: 'var(--ds-bg-card)',
                        border: '1px solid var(--ds-border)',
                      }}
                      onClick={() => navigate(`/stories/${id}/chapters/${chapter.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-medium truncate"
                          style={{ color: 'var(--ds-text-primary)' }}
                        >
                          {chapter.title || `${vocab.container} ${idx + 1}`}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full ${statusColors[chapter.status] || ''}`}
                          >
                            {chapter.status?.replace('_', ' ')}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                            {chapter.wordCount?.toLocaleString() || 0} {t('stories.words', 'words')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canEdit && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChapter(chapter.id, -1);
                              }}
                              disabled={
                                idx === 0 ||
                                (arr[idx - 1].actNumber ?? null) !== (chapter.actNumber ?? null)
                              }
                              aria-label={t('common.moveUp', 'Move up')}
                              className="disabled:opacity-30"
                            >
                              <ArrowUpIcon
                                className="w-4 h-4"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChapter(chapter.id, 1);
                              }}
                              disabled={
                                idx === arr.length - 1 ||
                                (arr[idx + 1].actNumber ?? null) !== (chapter.actNumber ?? null)
                              }
                              aria-label={t('common.moveDown', 'Move down')}
                              className="disabled:opacity-30"
                            >
                              <ArrowDownIcon
                                className="w-4 h-4"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              />
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (expandedChapter === chapter.id) {
                              setExpandedChapter(null);
                            } else {
                              setExpandedChapter(chapter.id);
                              fetchScenes(chapter.id);
                            }
                          }}
                          className="p-1 rounded"
                          style={{ color: 'var(--ds-text-secondary)' }}
                          title={vocab.subs}
                        >
                          {expandedChapter === chapter.id ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteChapter(chapter.id);
                            }}
                          >
                            <TrashIcon className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Scenes panel */}
                    {expandedChapter === chapter.id && (
                      <div
                        className="ml-4 mt-1 space-y-2 border-l-2 pl-3 py-2"
                        style={{ borderColor: 'var(--ds-border)' }}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className="text-xs font-semibold"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          >
                            {vocab.subs}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => {
                                setEditingScene(null);
                                setSceneSummary('');
                                setScenePov('');
                                setSceneArc('');
                                setShowSceneForm(chapter.id);
                              }}
                              className="text-xs text-primary-400 hover:text-primary-300"
                            >
                              <PlusIcon className="w-3 h-3 inline mr-0.5" />
                              {t('stories.scenes.add', 'Add')}
                            </button>
                          )}
                        </div>
                        {(pendingProposals[chapter.id] ?? []).map((p, pi) => (
                          <ProposalBox
                            key={pi}
                            proposal={p}
                            targetHasText={wordsIn(chapter.content) > 0}
                            onReview={() => setChatOpen(true)}
                          />
                        ))}
                        {canEdit &&
                          ((chapterScenes[chapter.id] || []).length > 0 ||
                            (chapter.wordCount || 0) > 0) && (
                            <div className="flex flex-wrap gap-2">
                              {/* Hidden once every empty beat has a draft waiting: pressing it
                                  again would replace drafts the writer is still reviewing. */}
                              {(chapterScenes[chapter.id] || []).some(
                                (b) => !wordsIn(b.content) && !pendingProposals[b.id],
                              ) && (
                                <button
                                  onClick={() =>
                                    startFocus(
                                      {
                                        kind: 'container',
                                        id: chapter.id,
                                        mode: 'draft',
                                        label: chapter.title || vocab.container,
                                      },
                                      acted
                                        ? t(
                                            'stories.ai.draftPrompt',
                                            'Write a rough draft of the dialogue for every {{beats}} in "{{title}}" that has no text yet.',
                                            {
                                              beats: vocab.subs.toLowerCase(),
                                              title: chapter.title || vocab.container,
                                            },
                                          )
                                        : t(
                                            'stories.ai.draftScenesPrompt',
                                            'Write a rough first draft of every scene in "{{title}}" that has no text yet.',
                                            { title: chapter.title || vocab.container },
                                          ),
                                    )
                                  }
                                  className="text-xs font-medium px-2 py-1 rounded bg-primary-500 hover:bg-primary-600 text-white"
                                >
                                  <SparklesIcon className="w-3 h-3 inline mr-0.5" />
                                  {acted
                                    ? t('stories.ai.draftDialogue', 'Draft dialogue')
                                    : t('stories.ai.draftScenes', 'Draft scenes')}
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  startFocus(
                                    {
                                      kind: 'container',
                                      id: chapter.id,
                                      mode: 'continue',
                                      label: chapter.title || vocab.container,
                                    },
                                    t(
                                      'stories.ai.continueContainerPrompt',
                                      'Continue "{{title}}" from where it leaves off.',
                                      { title: chapter.title || vocab.container },
                                    ),
                                  )
                                }
                                className="text-xs font-medium px-2 py-1 rounded border"
                                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-link)' }}
                              >
                                {t('stories.ai.continueContainer', 'Continue {{what}}', {
                                  what: vocab.container.toLowerCase(),
                                })}
                              </button>
                            </div>
                          )}
                        <div className="flex items-center gap-2">
                          <label className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                            {vocab.group}
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={50}
                            defaultValue={chapter.actNumber ?? ''}
                            disabled={!canEdit}
                            onBlur={async (e) => {
                              const raw = e.target.value.trim();
                              // Empty clears the assignment; otherwise 1-50.
                              const v = raw === '' ? null : parseInt(raw, 10);
                              if (v !== null && (!Number.isFinite(v) || v < 1 || v > 50)) return;
                              if (v === (chapter.actNumber ?? null)) return;
                              try {
                                await trackWrite(
                                  axios.patch(`/stories/${id}/chapters/${chapter.id}`, {
                                    actNumber: v,
                                  }),
                                );
                                fetchStory();
                              } catch {
                                toast.error(t('stories.saveError', 'Failed to save'));
                              }
                            }}
                            className="w-16 px-2 py-1 rounded border bg-transparent text-xs"
                            style={{
                              borderColor: 'var(--ds-border)',
                              color: 'var(--ds-text-primary)',
                            }}
                          />
                        </div>

                        {showSceneForm === chapter.id && (
                          <div
                            className="rounded-lg p-3 space-y-2"
                            style={{
                              background: 'var(--ds-bg-card)',
                              border: '1px solid var(--ds-border)',
                            }}
                          >
                            <input
                              type="text"
                              value={sceneSummary}
                              onChange={(e) => setSceneSummary(e.target.value)}
                              placeholder={t(
                                'stories.scenes.summaryPlaceholder',
                                'Scene summary...',
                              )}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-primary)',
                              }}
                            />
                            <select
                              value={scenePov}
                              onChange={(e) => setScenePov(e.target.value)}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-secondary)',
                              }}
                            >
                              <option value="">
                                {t('stories.scenes.povSelect', '— POV Character —')}
                              </option>
                              {story.characters?.map((c: any) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={sceneArc}
                              onChange={(e) => setSceneArc(e.target.value)}
                              placeholder={t('stories.scenes.arcPlaceholder', 'Emotional arc...')}
                              className="w-full px-2 py-1.5 rounded border bg-transparent text-xs"
                              style={{
                                borderColor: 'var(--ds-border)',
                                color: 'var(--ds-text-primary)',
                              }}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveScene(chapter.id)}
                                className="flex-1 py-1.5 bg-primary-500 text-white rounded text-xs font-medium"
                              >
                                {editingScene ? t('common.save', 'Save') : t('common.add', 'Add')}
                              </button>
                              <button
                                onClick={() => setShowSceneForm(null)}
                                className="px-3 py-1.5 rounded text-xs"
                                style={{ color: 'var(--ds-text-secondary)' }}
                              >
                                {t('common.cancel', 'Cancel')}
                              </button>
                            </div>
                          </div>
                        )}

                        {(chapterScenes[chapter.id] || []).map((scene) => {
                          const drafted = wordsIn(scene.content);
                          const proposals = pendingProposals[scene.id] ?? [];
                          const beatLabel =
                            (scene.summary || '').slice(0, 60) ||
                            t('stories.scenes.untitled', 'Untitled scene');
                          return (
                            <div
                              key={scene.id}
                              data-beat-id={scene.id}
                              className="rounded-lg p-3 flex items-start justify-between"
                              style={{
                                background: 'var(--ds-bg-card)',
                                border: '1px solid var(--ds-border)',
                              }}
                            >
                              <div className="flex-1 min-w-0">
                                <div
                                  className="text-xs font-medium whitespace-pre-wrap break-words"
                                  style={{ color: 'var(--ds-text-primary)' }}
                                >
                                  {scene.summary || t('stories.scenes.untitled', 'Untitled scene')}
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${drafted ? 'bg-green-500/20 text-green-600' : ''}`}
                                    style={
                                      drafted
                                        ? undefined
                                        : {
                                            border: '1px solid var(--ds-border)',
                                            color: 'var(--ds-text-secondary)',
                                          }
                                    }
                                  >
                                    {drafted
                                      ? t('stories.scenes.drafted', 'Drafted · {{words}} words', {
                                          words: drafted,
                                        })
                                      : t('stories.scenes.notDraftedBadge', 'Not drafted')}
                                  </span>
                                  {proposals.length > 0 && (
                                    <span
                                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                                      style={{
                                        border: '1px dashed var(--ds-accent, #c4a962)',
                                        color: 'var(--ds-text-secondary)',
                                      }}
                                    >
                                      {t('stories.scenes.proposalWaiting', 'Proposal waiting')}
                                    </span>
                                  )}
                                </div>
                                {scene.content && (
                                  <p
                                    className="text-xs mt-1 whitespace-pre-wrap overflow-hidden"
                                    style={{
                                      color: 'var(--ds-text-secondary)',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 4,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {scene.content}
                                  </p>
                                )}
                                {scene.emotionalArc && (
                                  <div
                                    className="text-[10px] mt-0.5"
                                    style={{ color: 'var(--ds-text-secondary)' }}
                                  >
                                    {scene.emotionalArc}
                                  </div>
                                )}
                                {scene.povCharacterId && (
                                  <div
                                    className="text-[10px] mt-0.5"
                                    style={{ color: 'var(--ds-text-secondary)' }}
                                  >
                                    POV:{' '}
                                    {story.characters?.find(
                                      (c: any) => c.id === scene.povCharacterId,
                                    )?.name || scene.povCharacterId}
                                  </div>
                                )}
                                {proposals.map((p, pi) => (
                                  <ProposalBox
                                    key={pi}
                                    proposal={p}
                                    targetHasText={drafted > 0}
                                    onReview={() => setChatOpen(true)}
                                  />
                                ))}
                                {canEdit && (
                                  <div className="flex gap-3 mt-2">
                                    <button
                                      onClick={() =>
                                        startFocus(
                                          {
                                            kind: 'beat',
                                            id: scene.id,
                                            mode: 'continue',
                                            label: beatLabel,
                                            fresh: !drafted,
                                          },
                                          drafted
                                            ? t(
                                                'stories.ai.continueBeatPrompt',
                                                'Continue "{{beat}}" from where it leaves off.',
                                                { beat: beatLabel },
                                              )
                                            : t('stories.ai.draftBeatPrompt', 'Draft "{{beat}}".', {
                                                beat: beatLabel,
                                              }),
                                        )
                                      }
                                      className="text-[11px] font-medium"
                                      style={{ color: 'var(--ds-link)' }}
                                    >
                                      {drafted
                                        ? t('stories.ai.continue', 'Continue')
                                        : t('stories.ai.draft', 'Draft')}
                                    </button>
                                    {drafted > 0 && (
                                      <button
                                        onClick={() =>
                                          startFocus(
                                            {
                                              kind: 'beat',
                                              id: scene.id,
                                              mode: 'rewrite',
                                              label: beatLabel,
                                            },
                                            t(
                                              'stories.ai.rewriteBeatPrompt',
                                              'Rewrite "{{beat}}".',
                                              {
                                                beat: beatLabel,
                                              },
                                            ),
                                          )
                                        }
                                        className="text-[11px] font-medium"
                                        style={{ color: 'var(--ds-link)' }}
                                      >
                                        {t('stories.ai.rewrite', 'Rewrite')}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                              {canEdit && (
                                <div className="flex items-center gap-1 ml-2 shrink-0">
                                  <button onClick={() => moveScene(chapter.id, scene.id, -1)}>
                                    <ChevronUpIcon
                                      className="w-3 h-3"
                                      style={{ color: 'var(--ds-text-secondary)' }}
                                    />
                                  </button>
                                  <button onClick={() => moveScene(chapter.id, scene.id, 1)}>
                                    <ChevronDownIcon
                                      className="w-3 h-3"
                                      style={{ color: 'var(--ds-text-secondary)' }}
                                    />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingScene(scene);
                                      setSceneSummary(scene.summary || '');
                                      setScenePov(scene.povCharacterId || '');
                                      setSceneArc(scene.emotionalArc || '');
                                      setShowSceneForm(chapter.id);
                                    }}
                                  >
                                    <PencilIcon
                                      className="w-3 h-3"
                                      style={{ color: 'var(--ds-text-secondary)' }}
                                    />
                                  </button>
                                  <button onClick={() => deleteScene(chapter.id, scene.id)}>
                                    <TrashIcon className="w-3 h-3 text-red-400" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {(chapterScenes[chapter.id] || []).length === 0 &&
                          showSceneForm !== chapter.id && (
                            <p
                              className="text-[11px] py-2"
                              style={{ color: 'var(--ds-text-secondary)' }}
                            >
                              {t('stories.scenes.empty', 'No scenes yet')}
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                ))}

              {(!story.chapters || story.chapters.length === 0) && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.noChapters', 'No chapters yet. Add your first one!')}
                </p>
              )}
            </div>
          )}

          {/* Plot Threads Tab */}
          {tab === 'threads' && (
            <div className="space-y-3">
              {canEdit && (
                <button
                  onClick={() => {
                    setEditingThread(null);
                    setThreadTitle('');
                    setThreadDescription('');
                    setThreadStatus('open');
                    setShowThreadForm(true);
                  }}
                  className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-medium transition-colors hover:border-primary-500"
                  style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                >
                  <PlusIcon className="w-4 h-4 inline mr-1" />
                  {t('stories.threads.add', 'Add Plot Thread')}
                </button>
              )}

              {showThreadForm && (
                <div
                  className="ds-card rounded-xl p-4 space-y-3"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <input
                    type="text"
                    value={threadTitle}
                    onChange={(e) => setThreadTitle(e.target.value)}
                    placeholder={t('stories.threads.titlePlaceholder', 'Thread title...')}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <textarea
                    value={threadDescription}
                    onChange={(e) => setThreadDescription(e.target.value)}
                    placeholder={t('stories.threads.descriptionPlaceholder', 'Description...')}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm resize-none"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  />
                  <select
                    value={threadStatus}
                    onChange={(e) => setThreadStatus(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                  >
                    <option value="open">{t('stories.threads.statusOpen', '🔴 Open')}</option>
                    <option value="developing">
                      {t('stories.threads.statusDeveloping', '🟡 Developing')}
                    </option>
                    <option value="resolved">
                      {t('stories.threads.statusResolved', '✅ Resolved')}
                    </option>
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={saveThread}
                      className="flex-1 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium"
                    >
                      {editingThread ? t('common.save', 'Save') : t('common.add', 'Add')}
                    </button>
                    <button
                      onClick={() => setShowThreadForm(false)}
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}

              {threads.map((thread, ti) => {
                const badge = threadStatusBadge[thread.status] || threadStatusBadge.open;
                return (
                  <div
                    key={thread.id}
                    className="ds-card rounded-xl p-4 flex items-start justify-between"
                    style={{
                      background: 'var(--ds-bg-card)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
                          {badge.icon} {thread.status}
                        </span>
                        <span
                          className="font-medium truncate"
                          style={{ color: 'var(--ds-text-primary)' }}
                        >
                          {thread.title}
                        </span>
                      </div>
                      {thread.description && (
                        <p
                          className="text-xs mt-1.5 line-clamp-2"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        >
                          {thread.description}
                        </p>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 ml-2 shrink-0">
                        <button
                          onClick={() => moveThread(thread.id, -1)}
                          disabled={ti === 0}
                          aria-label={t('common.moveUp', 'Move up')}
                          className="disabled:opacity-30"
                        >
                          <ArrowUpIcon
                            className="w-4 h-4"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          />
                        </button>
                        <button
                          onClick={() => moveThread(thread.id, 1)}
                          disabled={ti === threads.length - 1}
                          aria-label={t('common.moveDown', 'Move down')}
                          className="disabled:opacity-30"
                        >
                          <ArrowDownIcon
                            className="w-4 h-4"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          />
                        </button>
                        <button
                          onClick={() => {
                            setEditingThread(thread);
                            setThreadTitle(thread.title);
                            setThreadDescription(thread.description || '');
                            setThreadStatus(thread.status);
                            setShowThreadForm(true);
                          }}
                        >
                          <PencilIcon
                            className="w-4 h-4"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          />
                        </button>
                        <button onClick={() => deleteThread(thread.id)}>
                          <TrashIcon className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {threads.length === 0 && !showThreadForm && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.threads.empty', 'No plot threads yet. Track your story arcs!')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {/* The workshop chat rides every tab: docked above the navigation, opens as an overlay. */}
      {canEdit && !chatOpen && (
        <button
          ref={dockButtonRef}
          onClick={() => setChatOpen(true)}
          className="fixed bottom-16 left-0 right-0 z-30 mx-auto max-w-[428px] min-[840px]:max-w-[800px] flex items-center justify-center gap-2 py-2.5 px-4 rounded-t-xl text-sm font-medium shadow-lg"
          style={{
            background: 'var(--ds-bg-card)',
            border: '1px solid var(--ds-border)',
            borderBottom: 'none',
            color: 'var(--ds-text-primary)',
          }}
        >
          <SparklesIcon className="w-4 h-4 text-primary-400" />
          {t('stories.ai.openChat', 'Brainstorm with AI')}
          {brainstormMessages.length > 0 && (
            <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              · {brainstormMessages.length}
            </span>
          )}
        </button>
      )}
      {chatOpen && (
        <div
          ref={chatSheetRef}
          className="fixed inset-0 z-40 flex flex-col"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal={chatOnTop}
          aria-label={t('stories.ai.openChat', 'Brainstorm with AI')}
        >
          <button
            className="flex-1"
            aria-label={t('common.close', 'Close')}
            onClick={() => setChatOpen(false)}
          />
          <div
            className="h-[80vh] w-full max-w-[428px] min-[840px]:max-w-[800px] mx-auto flex flex-col overflow-y-auto rounded-t-2xl p-1 pb-4"
            style={{ background: 'var(--ds-bg-primary)', height: '80dvh' }}
          >
            <div className="flex justify-center py-1">
              <button
                onClick={() => setChatOpen(false)}
                aria-label={t('common.close', 'Close')}
                className="px-6"
              >
                <ChevronDownIcon
                  className="w-5 h-5"
                  style={{ color: 'var(--ds-text-secondary)' }}
                />
              </button>
            </div>
            {/* Brainstorm with AI */}
            <div
              className="ds-card rounded-2xl p-5 flex-1 min-h-0 flex flex-col"
              style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="w-5 h-5 text-primary-400" />
                <h3 className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                  {t('stories.ai.brainstormTitle', 'Brainstorm with AI')}
                </h3>
                {isOwner && brainstormMessages.length > 0 && (
                  <button
                    onClick={clearChat}
                    disabled={extracting || filing}
                    className="ml-auto text-xs disabled:opacity-50"
                    style={{ color: 'var(--ds-text-secondary)' }}
                  >
                    {t('stories.ai.clearChat', 'Clear conversation')}
                  </button>
                )}
              </div>

              <div ref={chatListRef} className="flex-1 min-h-0 space-y-3 mb-3 overflow-y-auto">
                {brainstormMessages.length === 0 && !brainstorming && (
                  <div
                    className="h-full flex items-center justify-center text-sm text-center px-6"
                    style={{ color: 'var(--ds-text-secondary)' }}
                  >
                    {t(
                      'stories.ai.emptyThread',
                      'Talk through your story — the AI remembers this conversation and can make changes for you.',
                    )}
                  </div>
                )}
                {brainstormMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`text-sm rounded-xl px-4 py-3 ${msg.role === 'user' ? 'ml-8' : 'mr-4'}`}
                    style={{
                      background:
                        msg.role === 'user'
                          ? 'color-mix(in srgb, var(--ds-accent, #8b5cf6) 15%, transparent)'
                          : 'var(--ds-bg-primary)',
                      color: 'var(--ds-text-primary)',
                      border: msg.role === 'ai' ? '1px solid var(--ds-border)' : 'none',
                    }}
                  >
                    <div className="whitespace-pre-wrap">{msg.text}</div>
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {msg.actions.map((action, ai) => (
                          <div
                            key={ai}
                            className="flex items-start gap-2 text-xs rounded-lg px-2 py-1.5"
                            style={{
                              background: 'var(--ds-bg-card)',
                              border: '1px solid var(--ds-border)',
                              color: 'var(--ds-text-secondary)',
                            }}
                          >
                            <span className="flex-1 min-w-0">
                              <span className="block truncate">
                                {action.status === 'applied' || action.status === 'committed'
                                  ? '✓ '
                                  : action.status === 'failed'
                                    ? '⚠ '
                                    : ''}
                                {action.summary}
                              </span>
                              {(action.tool === 'write_beat_content' ||
                                action.tool === 'write_container_content') &&
                                typeof action.args.content === 'string' &&
                                (action.status === 'pending' ||
                                  action.status === 'committed' ||
                                  action.status === 'dismissed') && (
                                  <details className="mt-1">
                                    <summary
                                      className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide"
                                      style={{ color: 'var(--ds-text-secondary)' }}
                                    >
                                      {action.status === 'committed'
                                        ? t('stories.ai.committedText', 'Committed text')
                                        : action.status === 'dismissed'
                                          ? t('stories.ai.notUsedText', 'Not used · not committed')
                                          : action.draft
                                            ? t(
                                                'stories.scenes.proposedDraft',
                                                'Proposed draft · not committed',
                                              )
                                            : action.args.mode === 'append'
                                              ? t(
                                                  'stories.scenes.proposedAddition',
                                                  'Proposed addition · not committed',
                                                )
                                              : t(
                                                  'stories.scenes.proposedRewrite',
                                                  'Proposed rewrite · not committed',
                                                )}
                                    </summary>
                                    <div
                                      className="mt-1 p-2 rounded whitespace-pre-wrap text-xs max-h-64 overflow-y-auto"
                                      style={{
                                        border:
                                          action.status === 'pending'
                                            ? '1px dashed var(--ds-accent, #c4a962)'
                                            : '1px solid var(--ds-border)',
                                        color: 'var(--ds-text-primary)',
                                        fontStyle:
                                          action.status === 'pending' ? 'italic' : 'normal',
                                      }}
                                    >
                                      {action.args.content}
                                    </div>
                                  </details>
                                )}
                            </span>
                            {action.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => commitAction(i, ai, action)}
                                  disabled={committing !== null}
                                  className="shrink-0 px-2 py-1 rounded bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium"
                                >
                                  {t('stories.ai.commit', 'Make this change')}
                                </button>
                                <button
                                  onClick={() => dismissAction(i, ai)}
                                  disabled={committing !== null}
                                  aria-label={t('stories.ai.dismiss', 'Dismiss')}
                                  className="shrink-0 disabled:opacity-50"
                                  style={{ color: 'var(--ds-text-secondary)' }}
                                >
                                  <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* A reply whose tools already changed the story (or still propose a change) has
                        nothing left to file — offering "Add to story" only invites duplicates.
                        Failed and dismissed actions changed nothing, so those replies keep it. */}
                    {msg.role === 'ai' &&
                      !msg.actions?.some(
                        (a) =>
                          a.status === 'applied' ||
                          a.status === 'committed' ||
                          a.status === 'pending',
                      ) &&
                      (extractedMsgs[msg.id || msg.text] ? (
                        <span
                          className="mt-2 text-xs font-medium flex items-center gap-1"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        >
                          ✓ {t('stories.ai.addedToStory', 'Added to story')}
                        </span>
                      ) : (
                        <button
                          onClick={() => startExtract(msg.text, msg.id || msg.text)}
                          disabled={extracting || filing}
                          className="mt-2 text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                          style={{ color: 'var(--ds-link)' }}
                        >
                          <SparklesIcon className="w-3.5 h-3.5" />
                          {extracting
                            ? t('stories.ai.extracting', 'Reading...')
                            : t('stories.ai.addToStory', 'Add to story')}
                        </button>
                      ))}
                  </div>
                ))}
                {brainstorming && (
                  <div className="flex items-center gap-2 px-4 py-2">
                    <div
                      className="animate-spin rounded-full h-4 w-4 border-b-2"
                      style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
                    />
                    <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                      {t('stories.ai.thinking', 'Thinking...')}
                    </span>
                  </div>
                )}
              </div>

              {chatFocus && (
                <div
                  className="flex items-center gap-2 text-xs rounded-lg px-2 py-1 mb-2"
                  style={{
                    border: '1px dashed var(--ds-accent, #c4a962)',
                    color: 'var(--ds-text-secondary)',
                  }}
                >
                  <span className="flex-1 min-w-0 truncate">
                    {chatFocus.mode === 'rewrite'
                      ? t('stories.ai.focusRewrite', 'Rewriting: {{label}}', {
                          label: chatFocus.label,
                        })
                      : chatFocus.mode === 'draft'
                        ? acted
                          ? t('stories.ai.focusDraft', 'Drafting dialogue: {{label}}', {
                              label: chatFocus.label,
                            })
                          : t('stories.ai.focusDraftScenes', 'Drafting scenes: {{label}}', {
                              label: chatFocus.label,
                            })
                        : chatFocus.fresh
                          ? t('stories.ai.focusDraftBeat', 'Drafting: {{label}}', {
                              label: chatFocus.label,
                            })
                          : t('stories.ai.focusContinue', 'Continuing: {{label}}', {
                              label: chatFocus.label,
                            })}
                  </span>
                  <button
                    onClick={() => setChatFocus(null)}
                    aria-label={t('stories.ai.focusClear', 'Stop focusing on this')}
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <div className="flex-1 relative">
                  <textarea
                    ref={brainstormBoxRef}
                    value={brainstormPrompt}
                    onChange={(e) => setBrainstormPrompt(e.target.value)}
                    onInput={autoGrow}
                    maxLength={8000}
                    rows={brainstormExpanded ? 8 : 2}
                    placeholder={t(
                      'stories.ai.brainstormPlaceholder',
                      'Talk it through — plot, characters, titles...',
                    )}
                    className={`w-full px-3 py-2 pr-8 rounded-lg border bg-transparent text-sm resize-y max-h-[35vh] ${brainstormExpanded ? 'min-h-[min(12rem,30vh)]' : ''}`}
                    style={{
                      borderColor: 'var(--ds-border)',
                      color: 'var(--ds-text-primary)',
                      maxHeight: '35dvh',
                    }}
                  />
                  <button
                    onClick={() => {
                      setBrainstormExpanded((v) => !v);
                      // autoGrow leaves an explicit inline height; clear it so the
                      // rows/min-height of the new state actually take effect.
                      if (brainstormBoxRef.current) brainstormBoxRef.current.style.height = '';
                    }}
                    className="absolute top-2 right-2"
                    aria-label={
                      brainstormExpanded
                        ? t('stories.ai.collapse', 'Smaller box')
                        : t('stories.ai.expand', 'Bigger box')
                    }
                  >
                    {brainstormExpanded ? (
                      <ChevronDownIcon
                        className="w-4 h-4"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      />
                    ) : (
                      <ChevronUpIcon
                        className="w-4 h-4"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      />
                    )}
                  </button>
                </div>
                <button
                  onClick={sendBrainstorm}
                  disabled={brainstorming || extracting || filing || !brainstormPrompt.trim()}
                  className="p-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white transition-colors"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {extractResult && (
        <div className="fixed inset-0 z-50 flex p-3" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div
            className="m-auto w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl p-5 space-y-4"
            style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                {t('stories.ai.extractTitle', 'Found in your brainstorm')}
              </h3>
              <button
                onClick={() => {
                  extractReq.current++;
                  setExtractResult(null);
                  setExtractNote('');
                }}
                aria-label={t('common.close', 'Close')}
              >
                <XMarkIcon className="w-5 h-5" style={{ color: 'var(--ds-text-secondary)' }} />
              </button>
            </div>
            <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'stories.ai.extractHint',
                'Pick what to add — nothing is saved until you confirm.',
              )}
            </p>
            {extractResult.characters.length === 0 && extractResult.plotThreads.length === 0 && (
              <p className="text-sm py-4" style={{ color: 'var(--ds-text-secondary)' }}>
                {t('stories.ai.extractEmpty', 'Nothing structured found in that message.')}
              </p>
            )}
            {extractResult.characters.length > 0 && (
              <div>
                <div
                  className="text-xs font-semibold uppercase tracking-wide mb-2"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.ai.extractCharacters', 'Characters')}
                </div>
                <div className="space-y-2">
                  {extractResult.characters.map((c, i) => (
                    <label key={`c${i}`} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!extractChecked[`c${i}`]}
                        onChange={(e) =>
                          setExtractChecked((prev) => ({ ...prev, [`c${i}`]: e.target.checked }))
                        }
                        className="mt-1"
                      />
                      <span className="text-sm" style={{ color: 'var(--ds-text-primary)' }}>
                        <span className="font-medium">{c.name}</span>
                        <span
                          className="ml-2 text-xs capitalize"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        >
                          {c.role.replace('_', ' ')}
                        </span>
                        {c.existing && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                            {t('stories.ai.alreadyInStory', 'Already in your story')}
                          </span>
                        )}
                        {c.description && (
                          <span
                            className="block text-xs mt-0.5"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          >
                            {c.description.slice(0, 160)}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {extractResult.plotThreads.length > 0 && (
              <div>
                <div
                  className="text-xs font-semibold uppercase tracking-wide mb-2"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t('stories.ai.extractThreads', 'Plot threads')}
                </div>
                <div className="space-y-2">
                  {extractResult.plotThreads.map((th, i) => (
                    <label key={`t${i}`} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!extractChecked[`t${i}`]}
                        onChange={(e) =>
                          setExtractChecked((prev) => ({ ...prev, [`t${i}`]: e.target.checked }))
                        }
                        className="mt-1"
                      />
                      <span className="text-sm" style={{ color: 'var(--ds-text-primary)' }}>
                        <span className="font-medium">{th.title}</span>
                        {th.existing && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                            {t('stories.ai.alreadyInStory', 'Already in your story')}
                          </span>
                        )}
                        {th.description && (
                          <span
                            className="block text-xs mt-0.5"
                            style={{ color: 'var(--ds-text-secondary)' }}
                          >
                            {th.description.slice(0, 160)}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={extractNote}
                onChange={(e) => setExtractNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (
                    e.key === 'Enter' &&
                    extractNote.trim() &&
                    extractSource &&
                    !extracting &&
                    !filing
                  ) {
                    e.preventDefault();
                    startExtract(extractSource.text, extractSource.msgKey, extractNote);
                  }
                }}
                maxLength={2000}
                placeholder={t(
                  'stories.ai.extractFixPlaceholder',
                  'Got something wrong? Tell it — e.g. "Matt and Matt Cardigan Smith are the same person"',
                )}
                className="flex-1 px-3 py-2 rounded-lg border bg-transparent text-xs"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
              />
              <button
                onClick={() =>
                  extractSource &&
                  startExtract(extractSource.text, extractSource.msgKey, extractNote)
                }
                disabled={extracting || filing || !extractNote.trim() || !extractSource}
                className="px-3 py-2 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-link)' }}
              >
                {extracting
                  ? t('stories.ai.extracting', 'Reading...')
                  : t('stories.ai.extractReanalyze', 'Re-analyze')}
              </button>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={fileExtract}
                disabled={filing || extracting || !Object.values(extractChecked).some(Boolean)}
                className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {filing
                  ? t('common.saving', 'Saving...')
                  : t('stories.ai.extractAddBtn', 'Add selected')}
              </button>
              <button
                onClick={() => {
                  extractReq.current++;
                  setExtractResult(null);
                  setExtractNote('');
                }}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--ds-text-secondary)' }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {restoreTarget && (
        <div className="fixed inset-0 z-50 flex p-3" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div
            className="m-auto w-full max-w-md rounded-2xl p-5 space-y-3"
            style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
          >
            <h3 className="text-base font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
              {t('stories.history.restoreTitle', 'Restore this snapshot?')}
            </h3>
            <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'stories.history.restoreBody',
                'The story becomes exactly "{{label}}". What you have now is saved as its own snapshot first, so nothing is lost.',
                { label: restoreTarget.label },
              )}
            </p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={restoreSnapshot}
                disabled={restoring}
                className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {restoring
                  ? t('common.saving', 'Saving...')
                  : t('stories.history.restoreConfirm', 'Yes, restore')}
              </button>
              <button
                onClick={() => {
                  // Backing out mid-drain aborts the restore before it posts,
                  // without invalidating unrelated in-flight writes.
                  if (restoring) restoreToken.current++;
                  setRestoreTarget(null);
                }}
                disabled={restorePosting}
                className="px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                style={{ color: 'var(--ds-text-secondary)' }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {outOfWords && <OutOfWordsSheet info={outOfWords} onClose={() => setOutOfWords(null)} />}
    </MobileLayout>
  );
};

export default StoryWorkspace;
